import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const Injectables = () => {
  const navigate = useNavigate();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const pricingOptions = [
    {
      title: 'Neuromodulators (Botox/Jeuveau)',
      price: '$12',
      unit: 'per unit'
    },
    {
      title: 'Preventative Baby Botox',
      price: '$375',
      unit: 'per session'
    },
    {
      title: 'Dermal Fillers (Evolysse)',
      price: '$599',
      unit: 'per syringe'
    },
    {
      title: 'Plasma Rich Protein (Face & Neck)',
      price: '$500',
      unit: 'per session'
    },
    {
      title: 'PRP for Hair Restoration',
      price: '$500',
      unit: 'per session (Recommended 4 sessions)'
    }
  ];

  const benefits = [
    {
      title: 'Immediate Visible Results',
      description: 'See noticeable improvements in wrinkles, volume loss, and skin quality within days to weeks of treatment.'
    },
    {
      title: 'Non-Surgical Solution',
      description: 'Achieve dramatic aesthetic enhancements without surgery, incisions, or extended recovery time.'
    },
    {
      title: 'Minimal Downtime',
      description: 'Quick procedures with little to no downtime, allowing you to resume normal activities immediately.'
    },
    {
      title: 'Wrinkle Reduction',
      description: 'Effectively smooths fine lines and wrinkles on the forehead, around eyes, and between brows.'
    },
    {
      title: 'Volume Restoration',
      description: 'Restores lost facial volume in cheeks, lips, and under-eye areas for a youthful appearance.'
    },
    {
      title: 'Preventative Anti-Aging',
      description: 'Prevents new wrinkles from forming by relaxing facial muscles and stimulating collagen production.'
    },
    {
      title: 'Natural-Looking Enhancement',
      description: 'Achieves subtle, natural results that enhance your features without looking overdone.'
    },
    {
      title: 'Customizable Treatments',
      description: 'Tailored to your specific concerns, facial structure, and aesthetic goals for optimal results.'
    },
    {
      title: 'Long-Lasting Effects',
      description: 'Results typically last 3-6 months for neuromodulators and 6-18 months for fillers.'
    },
    {
      title: 'Hair Growth Stimulation',
      description: 'PRP treatments promote natural hair regrowth and improve hair thickness and density.'
    },
    {
      title: 'Skin Quality Improvement',
      description: 'PRP facial treatments enhance skin texture, tone, and overall radiance through natural healing.'
    },
    {
      title: 'Safe and FDA-Approved',
      description: 'All products are FDA-approved with proven safety records and minimal side effects.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Injectables - Botox & Dermal Fillers | Century Aesthetics Forest Hills</title>
        <meta name="description" content="Expert injectable treatments including Botox, Juvederm, and dermal fillers. Natural-looking anti-aging results in Forest Hills, NY." />
        <meta name="keywords" content="injectables, botox, dermal fillers, juvederm, anti-aging injections, wrinkle treatment" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/injectables" />
        
        <meta property="og:title" content="Injectables - Botox & Fillers | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Expert injectable treatments including Botox, Juvederm, and dermal fillers. Natural-looking anti-aging results in Forest Hills, NY." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/injectables" />
      </Helmet>

      <div className="min-h-screen bg-white pt-28">

      {/* Main Content Section */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-lg overflow-hidden shadow-2xl h-[500px]">
                <img 
                  src="/b.png" 
                  alt="Injectable Treatments - Botox, Dermal Fillers, PRP" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-6">
                INJECTABLE TREATMENTS: BOTOX, DERMAL FILLERS & PRP THERAPY
              </h2>
              <div className="w-20 h-1 bg-gold mb-6"></div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Injectable treatments offer comprehensive anti-aging solutions through neuromodulators (Botox, Jeuveau), dermal fillers (Evolysse), and Plasma Rich Protein (PRP) therapy. These minimally invasive procedures effectively reduce wrinkles, restore facial volume, improve skin quality, and stimulate hair growth. Neuromodulators temporarily relax facial muscles to smooth fine lines and prevent new wrinkles, while dermal fillers add volume to lips, cheeks, and under-eye areas. PRP therapy uses your body's natural healing factors to rejuvenate skin and promote hair restoration.
                </p>
                <p>
                  All injectable treatments are performed by experienced professionals and require minimal downtime. Results from neuromodulators appear within days and last 3-6 months, while dermal fillers provide immediate volume with results lasting 6-18 months. PRP treatments show progressive improvements over several weeks as collagen production increases. Schedule your free consultation at Century Longevity & Aesthetics to discover which injectable treatment is right for your aesthetic goals.
                </p>
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="mt-8 bg-olive-darkest text-white px-8 py-4 rounded font-medium text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Now
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 bg-olive-darkest">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-white mb-4">Injectable Treatment Pricing</h2>
            <div className="w-20 h-1 bg-gold mx-auto mb-6"></div>
            <div className="inline-flex items-center gap-3 bg-gold/10 border-2 border-gold px-8 py-4 rounded-lg">
              <CheckCircle className="w-6 h-6 text-gold" />
              <p className="text-white text-lg font-medium">
                Free Consultation for All Injectable Services
              </p>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingOptions.map((option, index) => (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white rounded-lg p-8 shadow-xl text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gold/10 flex items-center justify-center">
                  <svg 
                    className="w-10 h-10 text-gold" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={1.5} 
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" 
                    />
                  </svg>
                </div>
                
                <h3 className="text-lg font-heading font-semibold text-olive-darkest mb-3 min-h-[3rem] flex items-center justify-center">
                  {option.title}
                </h3>
                <p className={`${option.price === 'FREE' || option.price === 'Contact Us' ? 'text-4xl' : 'text-5xl'} font-heading font-light text-gold mb-1`}>
                  {option.price}
                </p>
                {option.unit && (
                  <p className="text-sm font-body text-gray-600 mb-6 min-h-[2.5rem] flex items-center justify-center">
                    {option.unit}
                  </p>
                )}
                
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToContact}
                  className="w-full bg-olive-darkest text-white px-6 py-3 rounded-lg font-medium text-sm tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg"
                >
                  Book Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-heading font-bold text-olive-darkest mb-4">Benefits of Injectable Treatments</h2>
            <div className="w-20 h-1 bg-gold mx-auto"></div>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {/* Carousel Container */}
            <div className="relative">
              {/* Previous Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1))}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Previous benefit"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => setCurrentBenefitIndex((prev) => (prev === benefits.length - 1 ? 0 : prev + 1))}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 z-10 bg-white rounded-full p-3 shadow-lg hover:bg-gold hover:text-white transition-all duration-300"
                aria-label="Next benefit"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Carousel Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentBenefitIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-lg shadow-xl p-8 sm:p-12 min-h-[300px] flex flex-col justify-center"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <CheckCircle className="w-16 h-16 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-3xl font-semibold text-olive-darkest mb-4">
                        {benefits[currentBenefitIndex].title}
                      </h3>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        {benefits[currentBenefitIndex].description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {benefits.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBenefitIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentBenefitIndex 
                        ? 'w-8 bg-gold' 
                        : 'w-2 bg-gray-300 hover:bg-gold/50'
                    }`}
                    aria-label={`Go to benefit ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16 bg-olive-darkest text-white p-8 sm:p-10 md:p-12 rounded-xl shadow-2xl text-center"
          >
            <p className="text-base sm:text-lg leading-relaxed mb-8 text-white">
              Transform your appearance with our comprehensive injectable treatments. Reduce wrinkles, restore volume, and rejuvenate your skin!
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="w-full sm:w-auto bg-gold text-olive-darkest px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation mb-10"
            >
              Schedule Consultation
            </motion.button>

            <div className="w-24 h-0.5 bg-gold/30 mx-auto mb-8"></div>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-4 text-white">Have Questions?</h3>
            <p className="text-base sm:text-lg text-white mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed">
              Learn about injectable treatments, what to expect during your appointment, and which option is best for your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  navigate('/faq');
                  setTimeout(() => {
                    const element = document.getElementById('injectables');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-white px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-white hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                View FAQs
              </motion.button>
              <motion.button
                whileHover={{ y: -2, scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToContact}
                className="w-full sm:w-auto bg-white/10 text-white border-2 border-gold px-8 sm:px-10 md:px-12 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg tracking-wide hover:bg-gold hover:text-olive-darkest transition-all duration-300 shadow-lg hover:shadow-xl touch-manipulation"
              >
                Contact Us Directly
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Injectables;